import styles from 'css/src/components/common/media-badge.module.scss';

import React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import type { AssetContainerTypes, SourceEnum } from 'js/src/client';
import Tooltip from 'js/src/components/common/ui/Tooltip';
import UpsellIcon from 'js/src/components/common/ui/UpsellIcon';
import { LOCKED_FEATURES, MODAL_IDS, showModal } from 'js/src/data/modals';
import { ImmutableFromJS } from 'js/src/libs/consts';
import PremiumMediaChecker, { PREMIUM_LEVEL } from 'js/src/libs/premium-media-checker';
import type { AppDispatch } from 'js/src/libs/RootState';
import type { ShowModalPayload } from 'js/src/data/modals/modals-actions';

type PropsFromRedux = ConnectedProps<typeof connector>;

type OwnProps = {
    source: SourceEnum;
    assetType: AssetContainerTypes;
    className?: string;
};

const showMarketplaceUpgradeModal = (showModal: (params: ShowModalPayload) => void) => {
    showModal({
        modal_id: MODAL_IDS.FEATURE_LOCK,
        modal_context: ImmutableFromJS({
            featureName: LOCKED_FEATURES.MARKETPLACE_MEDIA,
        }),
    });
};

const showUnlimitedUpgradeModal = (showModal: (params: ShowModalPayload) => void) => {
    showModal({
        modal_id: MODAL_IDS.FEATURE_LOCK,
        modal_context: ImmutableFromJS({
            featureName: LOCKED_FEATURES.UNLIMITED_PREMIUM_MEDIA,
        }),
    });
};

export const MediaBadgePresentation = React.memo(function MediaBadgePresentation({
    source,
    assetType,
    className,
    showModal,
}: PropsFromRedux & OwnProps) {
    /*
    Desired behaviour:
        Free assets:
            collapsed: nothing
            hover: "FREE"

        Unlimited-use premium assets:
            No access:
                collapsed: DIAMOND
                hover: DIAMOND + "premium"
                tooltip: "Upgrade your plan to unlock unlimited access to 50 million premium photos and videos"
            Access:
                collapsed: nothing
                hover: DIAMOND + "free"
                tooltip: "Use for free, included as part of your subscription."

        Marketplace assets:
            No access:
                collapsed: CROWN
                hover: CROWN + "marketplace"
                tooltip: "Upgrade your plan to unlock access to 500 million photos and videos from the marketplace"
            Access:
                collapsed: CROWN
                hover: CROWN + "marketplace"
                tooltip: "Use in your video for 1 marketplace credit"
     */

    const showMarketplaceUpgradeModalCallback = React.useCallback(() => {
        showMarketplaceUpgradeModal(showModal);
    }, [showModal]);

    const showUnlimitedUpgradeModalCallback = React.useCallback(() => {
        showUnlimitedUpgradeModal(showModal);
    }, [showModal]);

    const badgeClassNames = React.useMemo(() => {
        return classNames(styles['media-badge'], className);
    }, [className]);

    const badgeType = React.useMemo(
        () => PremiumMediaChecker.badge_required_for_source(source, assetType),
        [source, assetType],
    );

    switch (badgeType) {
        case PREMIUM_LEVEL.UPGRADE_FOR_UNLIMITED:
            // user can't be able to use asset, so show badge w/ upgrade prompt
            return (
                <Tooltip
                    title={
                        'Upgrade your plan to unlock unlimited access to 50 million photos and videos'
                    }
                    delay={{ show: 500, hide: 0 }}
                >
                    <span
                        className={classNames(
                            badgeClassNames,
                            styles['clickable'],
                            'gold-on-hover',
                        )}
                        onClick={showUnlimitedUpgradeModalCallback}
                    >
                        <i translate="no" className="material-symbols-outlined">
                            diamond
                        </i>
                    </span>
                </Tooltip>
            );
        case PREMIUM_LEVEL.UPGRADE_FOR_MARKETPLACE:
            // user can't be able to use asset, so show badge w/ upgrade prompt
            return (
                <Tooltip
                    title={
                        'Upgrade your plan to unlock access to 500 million photos and videos from the marketplace'
                    }
                    delay={{ show: 500, hide: 0 }}
                >
                    <span
                        className={classNames(
                            badgeClassNames,
                            styles['clickable'],
                            'gold-on-hover',
                        )}
                        onClick={showMarketplaceUpgradeModalCallback}
                    >
                        <UpsellIcon isOutlined />
                    </span>
                </Tooltip>
            );
        case PREMIUM_LEVEL.MARKETPLACE:
            // show credit badge to inform user that it'll take a credit to use
            return (
                <Tooltip
                    title="Use in your video for 1 marketplace credit"
                    delay={{ show: 500, hide: 0 }}
                >
                    <span className={badgeClassNames}>
                        <UpsellIcon isOutlined />
                    </span>
                </Tooltip>
            );
        case PREMIUM_LEVEL.INCLUDED_WITH_SUBSCRIPTION:
            return (
                <Tooltip
                    title="Use for free, it's included as part of your subscription!"
                    delay={{ show: 500, hide: 0 }}
                >
                    <span className={classNames(badgeClassNames, 'hide-unless-hovering')}>
                        <i translate="no" className="material-symbols-outlined">
                            diamond
                        </i>
                        &nbsp;FREE
                    </span>
                </Tooltip>
            );
        case PREMIUM_LEVEL.FREE:
            // show free badge
            return (
                <span className={classNames(badgeClassNames, 'pe-none', 'hide-unless-hovering')}>
                    Free
                </span>
            );
        case PREMIUM_LEVEL.NONE:
        default:
            // no badge
            return null;
    }
});

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return bindActionCreators(
        {
            showModal,
        },
        dispatch,
    );
};

const connector = connect(undefined, mapDispatchToProps);

const MediaBadge = connector(MediaBadgePresentation);

export default MediaBadge;
